import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {Object} BulkOptionDef
 * @property {string} name
 * @property {number} position
 * @property {string[]} values
 */

/**
 * @typedef {Object} BulkQuantityRule
 * @property {number} min
 * @property {number | null} max
 * @property {number} increment
 */

/**
 * @typedef {Object} BulkVariant
 * @property {number} id
 * @property {boolean} available
 * @property {number} price
 * @property {number | null} compareAtPrice
 * @property {string} sku
 * @property {number} inventoryQuantity
 * @property {string[]} options
 * @property {BulkQuantityRule} quantityRule
 */

/**
 * @typedef {Object} BulkLineItem
 * @property {BulkVariant} variant
 * @property {number} quantity
 */

/**
 * @typedef {Object} BulkProductModel
 * @property {number} productId
 * @property {string} currency
 * @property {string} moneyFormat
 * @property {boolean} [taxesIncluded]
 * @property {BulkOptionDef[]} options
 * @property {BulkVariant[]} variants
 */

/**
 * @typedef {Object} ProductBulkVariantOrderRefs
 * @property {HTMLTableSectionElement} tbody
 * @property {HTMLButtonElement} addRowButton
 * @property {HTMLElement} globalError
 * @property {HTMLElement} liveRegion
 * @property {HTMLElement} [totalValue]
 * @property {HTMLElement} [totalWrap]
 * @property {HTMLElement} [totalVatShare]
 * @property {HTMLElement} [standardView]
 * @property {HTMLElement} [bulkView]
 * @property {HTMLButtonElement} [toggleBulkButton]
 * @property {HTMLButtonElement} [toggleStandardButton]
 */

/** @param {string[]} options */
function variantKeyFromOptions(options) {
  return JSON.stringify(options);
}

/**
 * Clothing size order. Each slot lists equivalent labels (e.g. "XXL" / "2XL").
 * Mirrors the logic in `snippets/variant-main-picker.liquid`.
 */
const SIZE_SLOTS = [
  ['XXXXXXS'],
  ['XXXXXS'],
  ['XXXXS'],
  ['XXXS', '3XS'],
  ['XXS', '2XS'],
  ['XS'],
  ['S'],
  ['M'],
  ['L'],
  ['XL'],
  ['XXL', '2XL'],
  ['XXXL', '3XL'],
  ['XXXXL', '4XL'],
  ['XXXXXL', '5XL'],
  ['XXXXXXL', '6XL'],
];

const SIZE_NAME_RE = /größe|grösse|groesse|^sizes?$|taille|talla|tamanho|tamaño|misura|rozmiar/i;

/**
 * Sort rank for a single size label. Alpha sizes rank first (in SIZE_SLOTS
 * order), numeric sizes after (numeric order), everything else is NaN.
 *
 * Combined numeric labels (e.g. "42/44", "42/46") are ranked by their first
 * number then their second, so they don't collapse to the same rank the way a
 * plain `parseInt("42/44")` (= 42) would.
 * @param {string} value
 * @returns {number}
 */
function sizeRank(value) {
  const norm = String(value).trim().toUpperCase().replace(/[\s-]/g, '');
  for (let i = 0; i < SIZE_SLOTS.length; i++) {
    if (SIZE_SLOTS[i].includes(norm)) return i;
  }
  if (/^\d/.test(norm)) {
    const nums = norm.match(/\d+/g) || [];
    const first = parseInt(nums[0], 10) || 0;
    const second = nums[1] ? Math.min(parseInt(nums[1], 10), 999) : 0;
    return 100 + first * 1000 + second;
  }
  return Number.NaN;
}

/**
 * Whether an option is a size option — by name, or when most of its values are
 * recognisable sizes.
 * @param {BulkOptionDef} option
 * @returns {boolean}
 */
function optionIsSizeLike(option) {
  if (option.name && SIZE_NAME_RE.test(option.name.trim())) return true;
  if (!Array.isArray(option.values)) return false;
  const recognised = option.values.filter((value) => !Number.isNaN(sizeRank(value))).length;
  return recognised >= 2 && recognised >= option.values.length / 2;
}

/**
 * Returns the option values sorted into a sensible size order. Unrecognised
 * values keep their original relative order at the end. Non-size options are
 * returned unchanged.
 * @param {BulkOptionDef} option
 * @returns {string[]}
 */
function sortedOptionValues(option) {
  if (!Array.isArray(option.values) || !optionIsSizeLike(option)) {
    return option.values;
  }
  return option.values
    .map((value, index) => ({ value, index }))
    .sort((a, b) => {
      // Unrecognised values keep their original order at the very end (sentinel
      // stays above the largest possible numeric rank).
      const ra = Number.isNaN(sizeRank(a.value)) ? 5000000 + a.index : sizeRank(a.value);
      const rb = Number.isNaN(sizeRank(b.value)) ? 5000000 + b.index : sizeRank(b.value);
      return ra === rb ? a.index - b.index : ra - rb;
    })
    .map((entry) => entry.value);
}

/** Matches theme `icon-delete.svg` (stroke uses currentColor). */
const DELETE_ICON_SVG = `<span class="svg-wrapper" aria-hidden="true"><svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" focusable="false"><path d="M11.5 9v4.25M8.5 9v4.25M5.75 12.2V6h8.5c0 2.421 0 3.779 0 6.2 0 .853 0 1.447-.038 1.91-.037.453-.106.714-.207.911a2.498 2.498 0 0 1-.983 1.017c-.197.1-.458.17-.911.207-.463.037-1.057.038-1.91.038h-.4c-.853 0-1.447 0-1.91-.038-.453-.037-.714-.106-.911-.207a2.498 2.498 0 0 1-.984-1.017c-.1-.197-.17-.458-.207-.911C5.75 13.647 5.75 13.053 5.75 12.2z" stroke="currentColor" stroke-width="var(--icon-stroke-width, 1.5)" stroke-linecap="round"/><path d="M4.25 6h11.5M8 5.25a2 2 0 1 1 4 0" stroke="currentColor" stroke-width="var(--icon-stroke-width, 1.5)" stroke-linecap="round" stroke-linejoin="round"/></svg></span>`;

/**
 * @extends {Component<ProductBulkVariantOrderRefs>}
 */
class ProductBulkVariantOrder extends Component {
  requiredRefs = ['tbody', 'addRowButton', 'globalError', 'liveRegion'];

  /** @type {BulkProductModel | null} */
  #model = null;

  /** @type {Map<string, BulkVariant>} */
  #variantByKey = new Map();

  /** @type {string[]} */
  #defaultOptions = [];

  /** @type {number} */
  #rowCounter = 0;

  connectedCallback() {
    super.connectedCallback();

    if (!this.id) {
      this.id = `ace-product-bulk-variant-order-${this.dataset.productId || 'unknown'}`;
    }

    this.#parseModel();
    this.#buildVariantIndex();
    this.#parseDefaultOptions();

    this.refs.addRowButton?.addEventListener('click', this.#onAddRowClick);
    this.refs.tbody?.addEventListener('change', this.#onTbodyChange);
    this.refs.tbody?.addEventListener('click', this.#onTbodyClick);

    // Im Standardmodus speisen Variantenwechsel und das Mengenfeld der
    // Produktform die Gesamtsumme, beide liegen ausserhalb dieses Elements.
    // Die Plus/Minus-Buttons setzen input.value direkt, wodurch kein natives
    // change/input feuert — die Mengenaenderung kommt nur ueber dieses Event an.
    const section = this.#productSection();
    section?.addEventListener(ThemeEvents.variantUpdate, this.#onStandardSelectionChange);
    section?.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onStandardSelectionChange);
    section?.addEventListener('change', this.#onStandardSelectionChange);
    section?.addEventListener('input', this.#onStandardSelectionChange);

    this.#syncAddRowDisabled();
    if (this.#enableToggleMode()) {
      this.#setBulkModeActive(false);
      this.#initToggleMode();
    } else {
      this.#setBulkModeActive(true);
      this.#renderInitialRows();
    }

    this.#updateTotal();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.addRowButton?.removeEventListener('click', this.#onAddRowClick);
    this.refs.tbody?.removeEventListener('change', this.#onTbodyChange);
    this.refs.tbody?.removeEventListener('click', this.#onTbodyClick);
    this.refs.toggleBulkButton?.removeEventListener('click', this.#onToggleBulkClick);
    this.refs.toggleStandardButton?.removeEventListener('click', this.#onToggleStandardClick);

    const section = this.#productSection();
    section?.removeEventListener(ThemeEvents.variantUpdate, this.#onStandardSelectionChange);
    section?.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onStandardSelectionChange);
    section?.removeEventListener('change', this.#onStandardSelectionChange);
    section?.removeEventListener('input', this.#onStandardSelectionChange);

    this.#setBulkModeActive(false);
  }

  #onStandardSelectionChange = () => {
    if (this.#isBulkModeActive()) return;
    this.#updateTotal();
  };

  /** @returns {boolean} */
  #enableToggleMode() {
    return this.dataset.enableToggleMode === 'true';
  }

  #initToggleMode() {
    this.refs.toggleBulkButton?.addEventListener('click', this.#onToggleBulkClick);
    this.refs.toggleStandardButton?.addEventListener('click', this.#onToggleStandardClick);
  }

  /** @returns {HTMLElement | null} */
  #productSection() {
    const section = this.closest('.shopify-section');
    return section instanceof HTMLElement ? section : null;
  }

  /** @param {boolean} active */
  #setBulkModeActive(active) {
    this.dataset.bulkModeActive = active ? 'true' : 'false';
    this.#productSection()?.classList.toggle('ace-bulk-variant-order-active', active);
    if (!active) {
      this.#setMultipleVariantState(false);
    }
  }

  /** @param {boolean} active */
  #setMultipleVariantState(active) {
    this.#productSection()?.classList.toggle('ace-bulk-variant-order-multiple-variants', active);
    this.#findProductFormComponent()?.classList.toggle('ace-bulk-variant-order-multiple-variants', active);
  }

  /** @returns {HTMLElement | null} */
  #findProductFormComponent() {
    const productId = String(this.dataset.productId || this.#model?.productId || '');
    const section = this.#productSection();
    const scoped = section?.querySelector('product-form-component');
    if (scoped instanceof HTMLElement) return scoped;

    if (!productId) return null;
    const byProductId = document.querySelector(`product-form-component[data-product-id="${CSS.escape(productId)}"]`);
    return byProductId instanceof HTMLElement ? byProductId : null;
  }

  /** @returns {HTMLElement | null} */
  #findVariantPicker() {
    const embedded = this.refs.standardView?.querySelector('variant-picker');
    if (embedded instanceof HTMLElement) return embedded;

    const sectionPicker = this.#productSection()?.querySelector('variant-picker');
    return sectionPicker instanceof HTMLElement ? sectionPicker : null;
  }

  /**
   * Reads selected option value labels from the product `variant-picker` (buttons, dropdowns, or single-value rows).
   * @returns {string[]}
   */
  #getSelectedOptionValuesFromEmbeddedPicker() {
    const picker = this.#findVariantPicker();
    if (!picker || !this.#model?.options?.length) return [];

    const n = this.#model.options.length;
    /** @type {string[]} */
    const values = [];

    for (let i = 0; i < n; i++) {
      const fieldset = picker.querySelector(`fieldset[data-fieldset-index="${i}"]`);
      if (fieldset) {
        const input = fieldset.querySelector('input:checked');
        values.push(input instanceof HTMLInputElement ? input.value : '');
        continue;
      }

      const selects = picker.querySelectorAll('select.variant-option__select');
      const select = selects[i];
      if (select instanceof HTMLSelectElement) {
        values.push(select.value);
        continue;
      }

      const singleRows = picker.querySelectorAll('[data-testid="variant-option-single"]');
      const singleRow = singleRows[i];
      if (singleRow) {
        const valSpan = singleRow.querySelector('[data-testid="variant-option-single-value"]');
        values.push(valSpan?.textContent?.trim() ?? '');
        continue;
      }

      values.push('');
    }

    return values;
  }

  #onToggleBulkClick = () => {
    if (!this.#enableToggleMode()) return;

    const picked = this.#getSelectedOptionValuesFromEmbeddedPicker();
    const complete =
      picked.length === this.#model?.options?.length && picked.every((v) => v.length > 0);

    this.refs.standardView?.classList.add('hidden');
    this.refs.bulkView?.classList.remove('hidden');
    this.#setBulkModeActive(true);

    this.#renderInitialRows(complete ? picked : null, 2);
  };

  #onToggleStandardClick = () => {
    if (!this.#enableToggleMode()) return;

    this.#showStandardMode();
  };

  #parseModel() {
    const script = this.querySelector('[data-bulk-variant-order-json]');
    if (!(script instanceof HTMLScriptElement) || !script.textContent) return;

    try {
      this.#model = /** @type {BulkProductModel} */ (JSON.parse(script.textContent));
      if (this.#model?.options) {
        for (const option of this.#model.options) {
          option.values = sortedOptionValues(option);
        }
      }
    } catch {
      this.#model = null;
    }
  }

  #buildVariantIndex() {
    this.#variantByKey.clear();
    if (!this.#model?.variants) return;

    for (const variant of this.#model.variants) {
      this.#variantByKey.set(variantKeyFromOptions(variant.options), variant);
    }
  }

  #parseDefaultOptions() {
    const raw = this.dataset.defaultOptions;
    if (!raw) {
      this.#defaultOptions = [];
      return;
    }

    try {
      const parsed = JSON.parse(raw);
      this.#defaultOptions = Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
      this.#defaultOptions = [];
    }
  }

  /** @returns {boolean} */
  #showRowPrice() {
    return this.dataset.showRowPrice === 'true';
  }

  /** @returns {boolean} */
  #showRowSku() {
    return this.dataset.showRowSku === 'true';
  }

  /** @returns {boolean} */
  #showRowAvailability() {
    return this.dataset.showRowAvailability === 'true';
  }

  /** @returns {boolean} */
  #isBulkModeActive() {
    if (!this.#enableToggleMode()) return true;
    return Boolean(this.refs.bulkView && !this.refs.bulkView.classList.contains('hidden'));
  }

  /** @returns {number} */
  #maxRows() {
    const n = Number.parseInt(this.dataset.maxRows || '20', 10);
    return Number.isFinite(n) && n > 0 ? n : 20;
  }

  /** @returns {number} */
  #initialRows() {
    const n = Number.parseInt(this.dataset.initialRows || '1', 10);
    return Number.isFinite(n) && n > 0 ? n : 1;
  }

  /**
   * @param {string[] | null} [firstRowOptionsOverride]
   * @param {number | null} [rowCountOverride]
   */
  #renderInitialRows(firstRowOptionsOverride = null, rowCountOverride = null) {
    const tbody = this.refs.tbody;
    if (!tbody || !this.#model?.options?.length) return;

    tbody.replaceChildren();
    this.#rowCounter = 0;

    const requestedRows = rowCountOverride != null ? rowCountOverride : this.#initialRows();
    const count = Math.min(requestedRows, this.#maxRows());
    for (let i = 0; i < count; i++) {
      let initial;
      if (
        i === 0 &&
        firstRowOptionsOverride &&
        firstRowOptionsOverride.length === this.#model.options.length
      ) {
        initial = [...firstRowOptionsOverride];
      } else if (i === 0 && this.#defaultOptions.length === this.#model.options.length) {
        initial = [...this.#defaultOptions];
      } else {
        initial = this.#model.options.map(() => '');
      }
      this.#appendRow(tbody, initial);
    }

    this.#updateTotal();
  }

  /**
   * @param {HTMLElement} control
   * @param {'select' | 'quantity'} kind
   */
  #wrapField(control, kind) {
    const wrap = document.createElement('div');
    wrap.className = `ace-product-bulk-variant-order__field ace-product-bulk-variant-order__field--${kind}`;
    wrap.appendChild(control);
    return wrap;
  }

  /**
   * @param {HTMLSelectElement} select
   * @param {BulkQuantityRule} rule
   * @param {number} preferred
   */
  #populateQuantityOptions(select, rule, preferred) {
    const min = Number.isFinite(rule?.min) && rule.min > 0 ? rule.min : 1;
    const increment = Number.isFinite(rule?.increment) && rule.increment > 0 ? rule.increment : 1;
    const max = Number.isFinite(rule?.max) && rule.max > 0 ? rule.max : min + increment * 99;
    const cap = Math.max(min, max);

    select.replaceChildren();

    for (let qty = min; qty <= cap; qty += increment) {
      const option = document.createElement('option');
      option.value = String(qty);
      option.textContent = String(qty);
      select.appendChild(option);
    }

    const normalizedPreferred = preferred >= min ? preferred : min;
    const alignedPreferred = normalizedPreferred - ((normalizedPreferred - min) % increment);
    const targetValue = String(Math.min(cap, Math.max(min, alignedPreferred)));
    select.value = targetValue;
  }

  /**
   * Whether any variant carries this option value alongside the row's other selections.
   * Empty selections are wildcards, so a fresh row matches everything.
   *
   * @param {number} optionIndex - The option position (0-based)
   * @param {string} optionValue - The option value to check
   * @param {string[]} selectedOptions - Currently selected options for other positions
   * @param {boolean} requireAvailable - Whether the matching variant must be purchaseable
   * @returns {boolean}
   */
  #hasMatchingVariant(optionIndex, optionValue, selectedOptions, requireAvailable) {
    if (!this.#model?.variants) return true;

    // Build a partial options array with the value we're checking
    const testOptions = [...selectedOptions];
    testOptions[optionIndex] = optionValue;

    for (const variant of this.#model.variants) {
      if (requireAvailable && !variant.available) continue;

      let matches = true;
      for (let i = 0; i < testOptions.length; i++) {
        if (i === optionIndex) {
          // Must match the value we're checking
          if (variant.options[i] !== optionValue) {
            matches = false;
            break;
          }
        } else if (selectedOptions[i] && selectedOptions[i].length > 0) {
          // Must match already selected value
          if (variant.options[i] !== selectedOptions[i]) {
            matches = false;
            break;
          }
        }
        // Empty selections are wildcards
      }

      if (matches) return true;
    }

    return false;
  }

  /**
   * Check if an option value is available in any variant (or in combination with selected options)
   * @param {number} optionIndex - The option position (0-based)
   * @param {string} optionValue - The option value to check
   * @param {string[]} selectedOptions - Currently selected options for other positions
   * @returns {boolean} - Whether this option value is available
   */
  #isOptionValueAvailable(optionIndex, optionValue, selectedOptions) {
    return this.#hasMatchingVariant(optionIndex, optionValue, selectedOptions, true);
  }

  /**
   * Whether the combination exists as a variant at all, regardless of stock.
   * Sold out is not the same as non-existent: the first stays listed and disabled,
   * the second is dropped from the dropdown entirely.
   *
   * @param {number} optionIndex - The option position (0-based)
   * @param {string} optionValue - The option value to check
   * @param {string[]} selectedOptions - Currently selected options for other positions
   * @returns {boolean}
   */
  #optionValueExists(optionIndex, optionValue, selectedOptions) {
    return this.#hasMatchingVariant(optionIndex, optionValue, selectedOptions, false);
  }

  /**
   * Fills one option select with the values reachable from the row's other selections.
   *
   * Values whose combination does not exist are left out — on a sparse variant grid
   * (BW Clip: 490 grid slots, 64 real variants) listing them all as "unavailable"
   * buried the handful of real ones and had customers requesting articles that cannot
   * be built. Sold-out combinations do exist, so they stay listed and disabled.
   *
   * The list is rebuilt on every change rather than toggled, so values reappear once
   * the selection loosens again. The currently chosen value always survives, so a
   * rebuild can never silently drop the row's own state.
   *
   * @param {HTMLSelectElement} select
   * @param {number} optionIndex
   * @param {string[]} selectedOptions
   */
  #populateOptionSelect(select, optionIndex, selectedOptions) {
    const optDef = this.#model?.options[optionIndex];
    if (!optDef || !Array.isArray(optDef.values)) return;

    const chosen = selectedOptions[optionIndex] || '';
    const unavailableLabel = this.dataset.statusUnavailable || 'Nicht verfügbar';

    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = this.dataset.chooseOption || 'Auswählen';

    const optionElements = [placeholder];

    // A truncated variant list cannot prove a combination missing, so past the
    // 250 the JSON carries we only mark, never hide.
    const variantListComplete = (this.#model?.variants?.length ?? 0) < 250;

    for (const value of optDef.values) {
      if (
        variantListComplete &&
        value !== chosen &&
        !this.#optionValueExists(optionIndex, value, selectedOptions)
      ) {
        continue;
      }

      const option = document.createElement('option');
      option.value = value;

      if (this.#isOptionValueAvailable(optionIndex, value, selectedOptions)) {
        option.textContent = value;
      } else {
        option.textContent = `${value} (${unavailableLabel})`;
        option.classList.add('out-of-stock');
        option.disabled = true;
      }

      optionElements.push(option);
    }

    select.replaceChildren(...optionElements);
    select.value = chosen;
  }

  #appendRow(tbody, selectedOptions) {
    if (!this.#model) return;

    this.#rowCounter += 1;
    const rowId = `bulk-row-${this.#rowCounter}`;
    const tr = document.createElement('tr');
    tr.dataset.rowId = rowId;

    const optionCount = this.#model.options.length;

    for (let oi = 0; oi < optionCount; oi++) {
      const optDef = this.#model.options[oi];
      const td = document.createElement('td');
      const select = document.createElement('select');
      select.className = 'ace-product-bulk-variant-order__select';
      select.setAttribute('aria-label', `${optDef.name} — row ${this.#rowCounter}`);
      select.dataset.optionIndex = String(oi);

      this.#populateOptionSelect(select, oi, selectedOptions);

      td.appendChild(this.#wrapField(select, 'select'));
      tr.appendChild(td);
    }

    const qtyTd = document.createElement('td');
    qtyTd.className = 'ace-product-bulk-variant-order__qty-cell';
    const qtySelect = document.createElement('select');
    qtySelect.className = 'ace-product-bulk-variant-order__select';
    qtySelect.dataset.quantityInput = 'true';
    qtySelect.setAttribute(
      'aria-label',
      `${this.dataset.quantityLabel || 'Stück'} — Zeile ${this.#rowCounter}`
    );
    this.#populateQuantityOptions(
      qtySelect,
      { min: 1, max: null, increment: 1 },
      1
    );
    qtyTd.appendChild(this.#wrapField(qtySelect, 'select'));
    tr.appendChild(qtyTd);

    if (this.#showRowPrice()) {
      const ptd = document.createElement('td');
      ptd.className = 'ace-product-bulk-variant-order__price-cell';
      ptd.dataset.priceCell = 'true';
      ptd.textContent = '—';
      tr.appendChild(ptd);
    }

    if (this.#showRowSku()) {
      const std = document.createElement('td');
      std.className = 'ace-product-bulk-variant-order__sku-cell';
      std.dataset.skuCell = 'true';
      std.textContent = '—';
      tr.appendChild(std);
    }

    if (this.#showRowAvailability()) {
      const atd = document.createElement('td');
      atd.setAttribute('role', 'cell');
      const statusBtn = document.createElement('button');
      statusBtn.type = 'button';
      statusBtn.className = 'ace-product-bulk-variant-order__status-btn';
      statusBtn.dataset.statusTrigger = 'true';
      const dot = document.createElement('span');
      dot.className = 'ace-product-bulk-variant-order__status-dot ace-product-bulk-variant-order__status-dot--unavailable';
      dot.dataset.statusDot = 'true';
      dot.setAttribute('aria-hidden', 'true');
      statusBtn.appendChild(dot);
      const unavailableLabel = this.dataset.statusUnavailable || 'Nicht verfügbar';
      statusBtn.setAttribute('aria-label', unavailableLabel);
      statusBtn.setAttribute('title', unavailableLabel);
      atd.appendChild(statusBtn);
      tr.appendChild(atd);
    }

    const actionTd = document.createElement('td');
    actionTd.className = 'ace-product-bulk-variant-order__action-cell';
    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'button button-unstyled ace-product-bulk-variant-order__remove';
    removeBtn.dataset.removeRow = 'true';
    removeBtn.setAttribute('aria-label', `${this.dataset.removeRowLabel || 'Zeile entfernen'} ${this.#rowCounter}`);
    removeBtn.innerHTML = `${DELETE_ICON_SVG}<span class="visually-hidden">${this.dataset.removeRowLabel || 'Entfernen'}</span>`;
    actionTd.appendChild(removeBtn);
    tr.appendChild(actionTd);

    tbody.appendChild(tr);
    this.#refreshRow(tr);
    this.#syncRemoveButtons();
  }

  #syncRemoveButtons() {
    const tbody = this.refs.tbody;
    if (!tbody) return;

    const rows = tbody.querySelectorAll('tr');
    for (const btn of tbody.querySelectorAll('[data-remove-row]')) {
      if (!(btn instanceof HTMLButtonElement)) continue;
      btn.disabled = rows.length <= 1;
    }
  }

  /**
   * @param {string[]} options
   */
  #syncVariantPickerToOptions(options) {
    const picker = this.#findVariantPicker();
    if (!picker || !this.#model || options.length !== this.#model.options.length) return;

    for (let i = 0; i < options.length; i++) {
      const value = options[i];
      if (!value) continue;

      const fieldset = picker.querySelector(`fieldset[data-fieldset-index="${i}"]`);
      if (fieldset) {
        const input = fieldset.querySelector(`input[value="${CSS.escape(value)}"]`);
        if (input instanceof HTMLInputElement && !input.checked) {
          input.click();
        }
        continue;
      }

      const selects = picker.querySelectorAll('select.variant-option__select');
      const select = selects[i];
      if (select instanceof HTMLSelectElement && select.value !== value) {
        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  /**
   * @param {string[] | null} [selectedOptions]
   */
  #showStandardMode(selectedOptions = null) {
    if (selectedOptions) {
      this.#syncVariantPickerToOptions(selectedOptions);
    }

    this.refs.bulkView?.classList.add('hidden');
    this.refs.standardView?.classList.remove('hidden');
    this.#setBulkModeActive(false);

    this.refs.tbody?.replaceChildren();
    this.#rowCounter = 0;
    this.#clearGlobalError();
    this.#updateTotal();
    this.#syncAddRowDisabled();
  }

  /**
   * @param {HTMLTableRowElement} tr
   */
  #readRowOptions(tr) {
    if (!this.#model) return [];

    /** @type {string[]} */
    const out = [];
    const n = this.#model.options.length;

    for (let i = 0; i < n; i++) {
      const sel = tr.querySelector(`select[data-option-index="${i}"]`);
      if (sel instanceof HTMLSelectElement) {
        out.push(sel.value);
      } else {
        out.push('');
      }
    }

    return out;
  }

  /**
   * @param {HTMLTableRowElement} tr
   */
  #getRowQuantity(tr) {
    const q = tr.querySelector('[data-quantity-input]');
    if (q instanceof HTMLInputElement || q instanceof HTMLSelectElement) {
      const n = Number.parseInt(q.value, 10);
      return Number.isFinite(n) ? n : 0;
    }

    return 0;
  }

  /**
   * Recovers the integer net unit price from the displayed gross unit price.
   * Configured VAT factors are greater than 1, so unit grossing is injective at
   * cent precision and the original net cents can be recovered by division.
   *
   * @param {number} grossUnitCents
   * @returns {number}
   */
  #netUnitPrice(grossUnitCents) {
    const unitPrice = Math.max(0, Math.round(Number(grossUnitCents) || 0));
    const vatRate = Number(this.dataset.vatRate) || 1;
    return vatRate > 1 ? Math.round(unitPrice / vatRate) : unitPrice;
  }

  /**
   * @param {number} grossUnitCents
   * @param {number} quantity
   * @returns {number}
   */
  #cartRoundedLineTotal(grossUnitCents, quantity) {
    const netUnitPrice = this.#netUnitPrice(grossUnitCents);
    const lineQuantity = Math.max(0, Math.round(Number(quantity) || 0));
    const vatRate = Number(this.dataset.vatRate) || 1;
    return vatRate > 1
      ? Math.round(netUnitPrice * lineQuantity * vatRate)
      : netUnitPrice * lineQuantity;
  }

  #updateTotal() {
    const { totalValue, totalWrap, totalVatShare, tbody } = this.refs;
    if (!tbody || !this.#model) return;

    const sumOf = (/** @type {BulkLineItem[]} */ items) =>
      items.reduce((sum, line) => sum + this.#cartRoundedLineTotal(line.variant.price, line.quantity), 0);

    const bulkLines = this.#collectResolvedLineItems();
    const bulkTotal = sumOf(bulkLines);

    // Angezeigt wird die Summe des aktiven Modus; das Event unten behaelt die
    // Bulk-Semantik, damit die Personalisierung unveraendert darauf reagiert.
    const lines = this.#isBulkModeActive() ? bulkLines : this.#collectStandardLineItems();
    const total = this.#isBulkModeActive() ? bulkTotal : sumOf(lines);
    const canFormat = Boolean(this.#model.moneyFormat && this.#model.currency);

    if (totalWrap) totalWrap.hidden = lines.length === 0;

    if (totalValue && canFormat) {
      totalValue.textContent = formatMoney(total, this.#model.moneyFormat, this.#model.currency);
    }

    // Row prices are gross. For taxable countries, break the VAT share out per
    // actual cart line; export countries receive the country-aware "without VAT"
    // disclosure instead of hiding the note.
    if (totalVatShare && canFormat) {
      const vatRate = Number(this.dataset.vatRate) || 1;
      const template = this.dataset.vatShareTemplate || '';
      const taxNote = this.dataset.taxNote || '';

      if (vatRate > 1 && template) {
        const netTotal = lines.reduce(
          (sum, line) => sum + this.#netUnitPrice(line.variant.price) * line.quantity,
          0
        );
        const vatAmount = total - netTotal;
        totalVatShare.textContent = template.replace(
          '__AMOUNT__',
          formatMoney(vatAmount, this.#model.moneyFormat, this.#model.currency)
        );
        totalVatShare.hidden = false;
      } else if (taxNote) {
        totalVatShare.textContent = taxNote;
        totalVatShare.hidden = false;
      } else {
        totalVatShare.hidden = true;
      }
    }

    this.#dispatchBulkUpdate(bulkLines, bulkTotal);
  }

  /**
   * Update option availability in all select elements of a row based on current selections
   * @param {HTMLTableRowElement} tr
   * @param {string[]} currentOptions
   */
  #updateSelectOptionsAvailability(tr, currentOptions) {
    if (!this.#model?.options) return;

    const optionCount = this.#model.options.length;

    for (let oi = 0; oi < optionCount; oi++) {
      const select = tr.querySelector(`select[data-option-index="${oi}"]`);
      if (!(select instanceof HTMLSelectElement)) continue;

      this.#populateOptionSelect(select, oi, currentOptions);
    }
  }

  /**
   * @param {HTMLTableRowElement} tr
   */
  #refreshRow(tr) {
    if (!this.#model) return;

    this.#clearRowError(tr);

    const options = this.#readRowOptions(tr);
    const allChosen = options.every((v) => v.length > 0);
    const key = allChosen ? variantKeyFromOptions(options) : '';
    const variant = allChosen ? this.#variantByKey.get(key) : undefined;

    const qtyInput = tr.querySelector('[data-quantity-input]');

    const priceCell = tr.querySelector('[data-price-cell]');
    const skuCell = tr.querySelector('[data-sku-cell]');
    const statusDot = tr.querySelector('[data-status-dot]');
    const statusBtn = tr.querySelector('[data-status-trigger]');

    tr.dataset.variantId = variant ? String(variant.id) : '';
    tr.dataset.resolved = variant ? 'true' : 'false';

    // Update option availability in dropdowns whenever selections change
    this.#updateSelectOptionsAvailability(tr, options);

    if (!allChosen || !variant) {
      if (qtyInput instanceof HTMLSelectElement || qtyInput instanceof HTMLInputElement) qtyInput.disabled = true;
      if (priceCell) priceCell.textContent = '—';
      if (skuCell) skuCell.textContent = '—';
      if (statusDot) {
        statusDot.classList.add('ace-product-bulk-variant-order__status-dot--unavailable');
      }
      if (statusBtn) {
        const un = this.dataset.statusUnavailable || 'Nicht verfügbar';
        statusBtn.setAttribute('aria-label', un);
        statusBtn.setAttribute('title', un);
      }

      this.#updateTotal();
      return;
    }

    const rule = variant.quantityRule || { min: 1, max: null, increment: 1 };
    const isDisabled = !variant.available;

    if (qtyInput instanceof HTMLSelectElement) {
      qtyInput.disabled = isDisabled;
      const currentQuantity = this.#getRowQuantity(tr);
      this.#populateQuantityOptions(qtyInput, rule, currentQuantity > 0 ? currentQuantity : rule.min ?? 1);
    } else if (qtyInput instanceof HTMLInputElement) {
      qtyInput.disabled = isDisabled;
    }

    if (priceCell && this.#model.moneyFormat && this.#model.currency) {
      priceCell.textContent = formatMoney(variant.price, this.#model.moneyFormat, this.#model.currency);
    } else if (priceCell) {
      priceCell.textContent = '—';
    }

    if (skuCell) {
      skuCell.textContent = variant.sku || '—';
    }

    if (statusDot) {
      statusDot.classList.toggle('ace-product-bulk-variant-order__status-dot--unavailable', !variant.available);
    }
    if (statusBtn) {
      const availableLabel = this.dataset.statusAvailable || 'Verfügbar';
      const unavailableLabel = this.dataset.statusUnavailable || 'Nicht verfügbar';
      const label = variant.available ? availableLabel : unavailableLabel;
      statusBtn.setAttribute('aria-label', label);
      statusBtn.setAttribute('title', label);
    }

    this.#updateTotal();
  }

  #onTbodyChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement) && !(target instanceof HTMLInputElement)) return;

    const tr = target.closest('tr');
    if (!(tr instanceof HTMLTableRowElement)) return;

    this.#refreshRow(tr);
  };

  #onTbodyClick = (event) => {
    const target = event.target;

    const btn = target instanceof Element ? target.closest('[data-remove-row]') : null;
    if (!(btn instanceof HTMLButtonElement)) return;

    const tr = btn.closest('tr');
    const tbody = this.refs.tbody;
    if (!(tr instanceof HTMLTableRowElement) || !tbody) return;

    const rows = tbody.querySelectorAll('tr');
    if (rows.length <= 1) {
      for (const select of tr.querySelectorAll('select')) {
        if (select instanceof HTMLSelectElement) select.value = '';
      }

      const q = tr.querySelector('[data-quantity-input]');
      if (q instanceof HTMLInputElement || q instanceof HTMLSelectElement) {
        q.value = '1';
        q.disabled = true;
      }

      this.#refreshRow(tr);
      return;
    }

    tr.remove();
    this.#announce(this.dataset.removeRowLabel || 'Zeile entfernt');

    const remainingRows = tbody.querySelectorAll('tr');
    if (this.#enableToggleMode() && remainingRows.length === 1) {
      const remainingRow = remainingRows[0];
      if (remainingRow instanceof HTMLTableRowElement) {
        const options = this.#readRowOptions(remainingRow);
        if (options.every((value) => value.length > 0)) {
          this.#showStandardMode(options);
          return;
        }
      }
    }

    this.#syncRemoveButtons();
    this.#syncAddRowDisabled();
    this.#updateTotal();
  };

  #onAddRowClick = () => {
    const tbody = this.refs.tbody;
    if (!tbody || !this.#model) return;

    const current = tbody.querySelectorAll('tr').length;
    if (current >= this.#maxRows()) return;

    const empty = this.#model.options.map(() => '');
    this.#appendRow(tbody, empty);
    this.#syncAddRowDisabled();
    this.#syncRemoveButtons();
    this.#announce(this.dataset.addRowLabel || 'Variante hinzugefügt');
  };

  #syncAddRowDisabled() {
    const tbody = this.refs.tbody;
    const btn = this.refs.addRowButton;
    if (!tbody || !btn) return;

    const n = tbody.querySelectorAll('tr').length;
    btn.disabled = n >= this.#maxRows();
  }

  /**
   * @returns {BulkLineItem[]}
   */
  /**
   * The single line the standard (non-bulk) mode represents: the variant picked
   * in the product form times the quantity field next to the add-to-cart button.
   * @returns {BulkLineItem[]}
   */
  #collectStandardLineItems() {
    if (!this.#model?.options?.length) return [];

    let options = this.#getSelectedOptionValuesFromEmbeddedPicker();
    if (options.length !== this.#model.options.length) options = this.#defaultOptions;
    if (options.length !== this.#model.options.length) return [];
    if (!options.every((value) => typeof value === 'string' && value.length > 0)) return [];

    const variant = this.#variantByKey.get(variantKeyFromOptions(options));
    if (!variant || !variant.available) return [];

    const quantity = this.#getStandardQuantity();
    if (quantity < 1) return [];

    return [{ variant, quantity }];
  }

  /** @returns {number} */
  #getStandardQuantity() {
    const input = this.#findProductFormComponent()?.querySelector('input[name="quantity"]');
    if (!(input instanceof HTMLInputElement)) return 1;

    const parsed = Number(input.value);
    return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 1;
  }

  #collectResolvedLineItems() {
    if (!this.#model || !this.refs.tbody) return [];

    /** @type {Map<number, BulkLineItem>} */
    const merged = new Map();

    for (const tr of this.refs.tbody.querySelectorAll('tr')) {
      if (!(tr instanceof HTMLTableRowElement)) continue;

      const options = this.#readRowOptions(tr);
      if (!options.every((value) => value.length > 0)) continue;

      const variant = this.#variantByKey.get(variantKeyFromOptions(options));
      if (!variant || !variant.available) continue;

      const quantity = this.#getRowQuantity(tr);
      if (quantity < 1) continue;

      const previous = merged.get(variant.id);
      if (previous) {
        previous.quantity += quantity;
      } else {
        merged.set(variant.id, { variant, quantity });
      }
    }

    return [...merged.values()];
  }

  /** @returns {boolean} */
  #hasInvalidPartialRows() {
    if (!this.#model || !this.refs.tbody) return false;

    for (const tr of this.refs.tbody.querySelectorAll('tr')) {
      if (!(tr instanceof HTMLTableRowElement)) continue;

      const options = this.#readRowOptions(tr);
      const filled = options.filter((value) => value.length > 0).length;
      if (filled === 0) continue;

      const allChosen = options.every((value) => value.length > 0);
      if (!allChosen) return true;

      const variant = this.#variantByKey.get(variantKeyFromOptions(options));
      if (!variant || !variant.available) return true;

      const quantity = this.#getRowQuantity(tr);
      if (quantity < 1) return true;
    }

    return false;
  }

  /**
   * @param {BulkLineItem[]} lines
   * @param {number} garmentTotal
   */
  #dispatchBulkUpdate(lines, garmentTotal) {
    const active = this.#isBulkModeActive();
    this.#setMultipleVariantState(active && lines.length > 1);

    this.dispatchEvent(
      new CustomEvent('ace:bulk-variant-order:update', {
        bubbles: true,
        detail: {
          productId: this.dataset.productId || String(this.#model?.productId || ''),
          active,
          invalid: this.#hasInvalidPartialRows(),
          lines: lines.map((line) => ({
            variantId: line.variant.id,
            quantity: line.quantity,
            priceCents: line.variant.price,
          })),
          garmentTotal,
          quantity: lines.reduce((sum, line) => sum + line.quantity, 0),
        },
      })
    );
  }

  /**
   * @param {HTMLTableRowElement} tr
   * @param {string} message
   */
  #setRowError(tr, message) {
    tr.dataset.error = message;
    tr.classList.add('ace-product-bulk-variant-order__row--error');
    tr.title = message;
  }

  /**
   * @param {HTMLTableRowElement} tr
   */
  #clearRowError(tr) {
    delete tr.dataset.error;
    tr.classList.remove('ace-product-bulk-variant-order__row--error');
    tr.removeAttribute('title');
  }

  #clearGlobalError() {
    const el = this.refs.globalError;
    if (!el) return;

    el.textContent = '';
    el.classList.add('hidden');
  }

  /**
   * @param {string} message
   */
  #announce(message) {
    const el = this.refs.liveRegion;
    if (!el) return;

    el.textContent = '';
    window.requestAnimationFrame(() => {
      el.textContent = message;
    });
  }

}

customElements.define('ace-product-bulk-variant-order', ProductBulkVariantOrder);
